import React, { useState } from 'react';
import { Mail, Lock, User as UserIcon, Loader2, Eye, EyeOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../utils/api';
import { validatePassword } from '../utils/password';
import GoogleLogin from '../components/GoogleLogin';
import { LocalLink, useLocalizedNavigate } from '../i18n/useLocalizedNav';

const Register: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const navigate = useLocalizedNavigate();

  const passwordRules = validatePassword(formData.password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Client-side email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('auth_err_invalid_email'));
      setIsLoading(false);
      return;
    }

    // Client-side password validation
    const unmetRules = passwordRules.filter(r => !r.met);
    if (unmetRules.length > 0) {
      const rules = unmetRules
        .map(r => t(`password_rule_${r.id}`).toLowerCase())
        .join(', ');
      setError(t('auth_err_password_missing', { rules }));
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth_err_passwords_no_match'));
      setIsLoading(false);
      return;
    }

    try {
      const { confirmPassword, ...registerData } = formData;
      const res = await api.post('/auth/register', registerData);
      if (res.error) {
        setError(res.error);
      } else {
        navigate('/verify-email', { state: { email: formData.email } });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || t('auth_err_generic'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-2xl border border-warm-200 shadow-sm space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl">{t('register_title')}</h2>
        <p className="text-warm-500">{t('register_subtitle')}</p>
        <div className="bg-brand-accent/10 border border-brand-accent/20 rounded-xl p-3 text-[11px] text-warm-600 leading-relaxed">
          <span className="font-bold text-brand-accent block uppercase mb-1">{t('register_note_title')}</span>
          {t('register_note_1')} <span className="font-bold">{t('register_note_not')}</span> {t('register_note_2')}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('register_fullname_label')}</label>
          <div className="relative">
            <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input
              type="text"
              placeholder={t('register_fullname_placeholder')}
              className="w-full pl-10 pr-4 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.fullName}
              onChange={(e) => setFormData({...formData, fullName: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_email_label')}</label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input 
              type="email" 
              placeholder="name@example.com"
              className="w-full pl-10 pr-4 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              required
            />
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_password_label')}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full pl-10 pr-12 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? t('auth_hide_password') : t('auth_show_password')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-warm-500 hover:text-brand-accent transition-colors"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            {passwordRules.map(rule => (
              <div key={rule.id} className={`flex items-center space-x-2 text-[10px] font-bold uppercase transition-colors ${rule.met ? 'text-brand-success' : 'text-warm-500'}`}>
                <div className={`w-1 h-1 rounded-full ${rule.met ? 'bg-brand-success' : 'bg-warm-200'}`} />
                <span>{t(`password_rule_${rule.id}`)}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-bold text-warm-800 uppercase tracking-wider">{t('auth_confirm_password_label')}</label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-500" />
            <input 
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="w-full pl-10 pr-12 py-3 bg-warm-100 border-none rounded-xl focus:ring-2 focus:ring-brand-primary outline-none"
              value={formData.confirmPassword}
              onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              aria-label={showConfirmPassword ? t('auth_hide_password') : t('auth_show_password')}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-warm-500 hover:text-brand-accent transition-colors"
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-brand-danger/10 text-brand-danger p-3 rounded-xl text-xs font-bold border border-brand-danger/20">
            {error}
          </div>
        )}

        <button 
          type="submit" 
          disabled={isLoading}
          className="w-full py-3 bg-brand-primary text-brand-secondary font-heading font-bold rounded-xl shadow-lg hover:bg-brand-accent transition-all flex items-center justify-center disabled:opacity-50"
        >
          {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('register_submit')}
        </button>
      </form>

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-warm-200"></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-warm-500">{t('auth_or_continue')}</span>
        </div>
      </div>

      <GoogleLogin />

      <p className="text-center text-warm-500 text-sm">
        {t('register_have_account')} <LocalLink to="/login" className="text-brand-accent font-bold">{t('login_submit')}</LocalLink>
      </p>
    </div>
  );
};

export default Register;
